// Copyright (c) MobiFactory. All rights reserved.
// This source code is the property of MobiFactory. It is exclusively available to customers who have a valid user license.
// See LICENSE.txt file in the project root for full license information.

import NewEmailMessage from "../model/NewEmailMessage";
import NewEmailMessageAttachment from "../model/NewEmailMessageAttachment";
import emailMessagesApi from "../api/emailMessage/emailMessagesApi";
import { AttachmentType } from "../model/AttachmentType";
import EmailMessageRecipient from "../model/EmailMessageRecipient";
import { RecipientCategory } from "../model/RecipientCategory";
import { MailboxItem } from "../store/officeState";

/**
 * Associates email to contact
 * @param contactId
 * @param mailboxItem
 * @param callback
 */
async function associateEmailToContact(contactId: string, mailboxItem: MailboxItem, callback: () => void) {
  const coercionType = (await getEmailBodyFormat(mailboxItem)) ?? Office.CoercionType.Html;
  await mailboxItem?.body?.getAsync(coercionType, async (asyncResult: Office.AsyncResult<string>) => {
    await associateEmailToEntityCallback(asyncResult, contactId, null, null, null, mailboxItem);
    callback();
  });
}

/**
 * Associate email to entity callback
 * @param asyncResult
 * @param contactId
 * @param businessId
 * @param accountId
 * @param mailboxItem
 */
async function associateEmailToEntityCallback(
  asyncResult: Office.AsyncResult<string>,
  contactId: string | null,
  businessId: string | null,
  accountId: string | null,
  leadId: string | null,
  mailboxItem: MailboxItem
) {
  if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
    const item = mailboxItem;
    if (item) {
      const emailMessage: NewEmailMessage = {
        contactId: contactId,
        businessId: businessId,
        accountId: accountId,
        leadId: leadId,
        senderEmail: item.from.emailAddress,
        senderName: item.from.displayName,
        subject: item.subject.toString(),
        body: asyncResult.value.toString(),
        sendingDate: item.dateTimeCreated.toUTCString(),
        messageId: item.itemId,
        recipients: getEmailMessageRecipients(item),
      };
      const postEmailMessageResult = await emailMessagesApi.postEmailMessage(emailMessage);
      if (postEmailMessageResult.isSuccess) {
        // Save attachments
        const attachments = mailboxItem?.attachments;
        if (!attachments) {
          return;
        }
        attachments.forEach((attachment) => {
          mailboxItem?.getAttachmentContentAsync(attachment.id, async (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
              await postMessageAttachment(postEmailMessageResult.data, attachment, asyncResult.value.content);
            }
          });
        });
      }
    }
  }
}

/**
 * Post message attachment
 * @param emailMessageId
 * @param attachment
 * @param attachmentContent
 */
async function postMessageAttachment(
  emailMessageId: string,
  attachment: Office.AttachmentDetails,
  attachmentContent: string
) {
  const emailMessageAttachment: NewEmailMessageAttachment = {
    EmailMessageId: emailMessageId,
    AttachmentId: attachment.id,
    FileName: attachment.name,
    FileMimeType: attachment.contentType,
    FileSize: attachment.size,
    AttachmentType: getAttachmentType(attachment.attachmentType as Office.MailboxEnums.AttachmentType),
    IsInline: attachment.isInline,
  };
  const attachmentResult = await emailMessagesApi.postEmailMessageAttachment(emailMessageId, emailMessageAttachment);
  if (attachmentResult.isSuccess) {
    const base64Response = await fetch(`data:${attachment.contentType};base64,${attachmentContent}`);
    const blob = await base64Response.blob();
    await emailMessagesApi.postEmailMessageAttachmentFile(attachmentResult.data, blob);
  }
}

/**
 * Gets attachment type from office attachment type
 */
function getAttachmentType(officeAttachmentType: Office.MailboxEnums.AttachmentType): AttachmentType {
  switch (officeAttachmentType) {
    case Office.MailboxEnums.AttachmentType.Cloud:
      return AttachmentType.Cloud;
    case Office.MailboxEnums.AttachmentType.Item:
      return AttachmentType.Item;
    default:
      return AttachmentType.File;
  }
}

/**
 * Get email recipients
 * @param item
 */
function getEmailMessageRecipients(item: Office.MessageRead): EmailMessageRecipient[] {
  const tos = item.to.map((r) => ({
    emailAddress: r.emailAddress,
    displayName: r.displayName,
    category: RecipientCategory.to,
  }));
  const ccs = item.cc.map((r) => ({
    emailAddress: r.emailAddress,
    displayName: r.displayName,
    category: RecipientCategory.cc,
  }));
  return tos.concat(ccs);
}

/**
 * Associates email to business
 * @param businessId
 * @param mailboxItem
 * @param callback
 */
async function associateEmailToBusiness(businessId: string, mailboxItem: MailboxItem, callback: () => void) {
  const coercionType = (await getEmailBodyFormat(mailboxItem)) ?? Office.CoercionType.Html;
  await mailboxItem?.body?.getAsync(coercionType, async (asyncResult: Office.AsyncResult<string>) => {
    await associateEmailToEntityCallback(asyncResult, null, businessId, null, null, mailboxItem);
    callback();
  });
}

/**
 * Associates email to contact
 * @param accountId
 * @param mailboxItem
 * @param callback
 */
async function associateEmailToAccount(accountId: string, mailboxItem: MailboxItem, callback: () => void) {
  const coercionType = (await getEmailBodyFormat(mailboxItem)) ?? Office.CoercionType.Html;
  await mailboxItem?.body?.getAsync(coercionType, async (asyncResult: Office.AsyncResult<string>) => {
    await associateEmailToEntityCallback(asyncResult, null, null, accountId, null, mailboxItem);
    callback();
  });
}

/**
 * Associates email to lead
 * @param leadId
 * @param mailboxItem
 * @param callback
 */
async function associateEmailToLead(leadId: string, mailboxItem: MailboxItem, callback: () => void) {
  const coercionType = (await getEmailBodyFormat(mailboxItem)) ?? Office.CoercionType.Html;
  await mailboxItem?.body?.getAsync(coercionType, async (asyncResult: Office.AsyncResult<string>) => {
    await associateEmailToEntityCallback(asyncResult, null, null, null, leadId, mailboxItem);
    callback();
  });
}

async function getEmailBodyFormat(mailboxItem: MailboxItem): Promise<Office.CoercionType> {
  return new Promise((resolve) => {
    mailboxItem?.body?.getAsync(Office.CoercionType.Html, (htmlResult) => {
      if (htmlResult.status === Office.AsyncResultStatus.Succeeded && htmlResult.value.trim() !== "") {
        resolve(Office.CoercionType.Html);
      } else {
        resolve(Office.CoercionType.Text);
      }
    });
  });
}

export function useEmailService() {
  return {
    associateEmailToContact,
    associateEmailToBusiness,
    associateEmailToAccount,
    associateEmailToLead,
  };
}
